import { useForm, SubmitHandler } from "react-hook-form";
import { supabase } from "../supabaseClient";
import { useNavigate, useLocation, Link } from "react-router-dom";
import LogoFull from "../assets/img/LogoFull.png";
import { useEffect, useState } from "react";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import i18n from "../i18n";

type FormInputs = {
  email: string;
  password: string;
};

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const Login = () => {
  const { t } = useI18nSafe();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const safeRedirect = redirectParam && redirectParam.startsWith("/") ? redirectParam : null;

  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [resetStatus, setResetStatus] = useState<"idle" | "ok" | "err">("idle");

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setLoginError(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (error) {
      // On affiche le message Supabase brut (souvent en anglais). Optionnel: mapper vers t() si besoin.
      setLoginError(error.message);
      return;
    }

    // Si on vient d'un lien nécessitant une connexion (ex: invitation), on y retourne en priorité.
    if (safeRedirect) {
      navigate(safeRedirect);
      return;
    }

    // Version club en phase de test : tout le monde arrive sur l'app coach.
    // Les membres d'un club accèdent au club via la navbar ou /loginclub.
    // TODO: réactiver la redirection vers /club/:clubId/dashboard au lancement du club.

    navigate("/dashboard");
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setResetStatus("err");
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    if (error) {
      setResetStatus("err");
    } else {
      setResetStatus("ok");
      setTimeout(() => {
        setShowReset(false);
        setResetEmail("");
        setResetStatus("idle");
      }, 1200);
    }
  };

  return (
    <section className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <img
        src={LogoFull}
        alt="SwishData Logo"
        className="w-40 h-40 rounded-lg mb-4 ring-2 ring-orange-100 dark:ring-orange-900/40"
      />

      <h1 className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-6">
        {t("login:title")}
      </h1>

      <form
        className="w-full max-w-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm p-6"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        autoComplete="on"
      >
        {/* Email */}
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium mb-1">
            {t("login:labels.email")}
          </label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="username email"
            autoCapitalize="none"
            autoCorrect="off"
            placeholder={t("login:placeholders.email")}
            aria-invalid={!!errors.email || undefined}
            {...register("email", { required: t("login:errors.email_required") })}
            className="border border-gray-300 dark:border-gray-700 rounded-lg w-full p-3
             bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
             placeholder-gray-400 dark:placeholder-gray-500
             focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
          {errors.email && (
            <span className="mt-1 text-red-500 text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.email.message as string}
            </span>
          )}
        </div>

        {/* Password */}
        <div className="mb-4">
          <label htmlFor="password" className="block text-sm font-medium mb-1">
            {t("login:labels.password")}
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPwd ? "text" : "password"}
              autoComplete="current-password"
              placeholder={t("login:placeholders.password")}
              aria-invalid={!!errors.password || undefined}
              {...register("password", { required: t("login:errors.password_required") })}
              className="border border-gray-300 dark:border-gray-700 rounded-lg w-full p-3 pr-11
             bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
             placeholder-gray-400 dark:placeholder-gray-500
             focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              aria-label={showPwd ? t("login:a11y.hide_password") : t("login:a11y.show_password")}
            >
              {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.password && (
            <span className="mt-1 text-red-500 text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.password.message as string}
            </span>
          )}
        </div>

        {/* Erreur de login */}
        {loginError && (
          <div className="mb-4 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm p-2">
            {loginError}
          </div>
        )}

        <div className="flex justify-center mb-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-lg
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors"
          >
            {isSubmitting ? t("login:cta.signing_in") : t("login:cta.sign_in")}
          </button>
        </div>

        <p
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer text-center"
          onClick={() => {
            setShowReset(true);
            setResetStatus("idle");
          }}
        >
          {t("login:forgot_password")}
        </p>
      </form>

      <div className="mt-4">
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          {t("login:no_account")}{" "}
          <Link
            to={safeRedirect ? `/signup?redirect=${encodeURIComponent(safeRedirect)}` : "/signup"}
            className="text-orange-600 dark:text-orange-400 font-semibold hover:underline"
          >
            {t("login:sign_up_link")}
          </Link>
        </p>
      </div>

      {/* Modale Reset */}
      {showReset && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-800 p-6 rounded-2xl w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-semibold mb-4 text-center text-orange-600 dark:text-orange-400">
              {t("login:reset.title")}
            </h2>

            {resetStatus !== "idle" && (
              <div
                className={`mb-3 rounded-lg border p-2 text-sm ${resetStatus === "ok"
                  ? "border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-900/20 dark:text-green-300"
                  : "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
                  }`}
              >
                {resetStatus === "ok"
                  ? t("login:reset.success")
                  : t("login:reset.error")}
              </div>
            )}

            <input
              type="email"
              placeholder={t("login:reset.placeholder")}
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900
                         placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400 mb-4"
            />
            <div className="flex justify-between gap-2">
              <button
                onClick={handleResetPassword}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
              >
                {t("login:reset.send")}
              </button>
              <button
                onClick={() => {
                  setShowReset(false);
                  setResetEmail("");
                  setResetStatus("idle");
                }}
                className="border border-gray-300 dark:border-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                {t("login:reset.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
